/* eslint-disable jsx-a11y/no-autofocus */
// External Dependencies
import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import Multiselect from 'multiselect-react-dropdown';
import Spinner from 'sushiweb/Spinner/Spinner';

// Internal Dependencies
import { InvalidSearchImg } from '../../../../images/images';
import { useMutationCall, useQueryCall } from '../../../../api/react-client';
import { EmptyState } from '../../../../components/commons';

// UI Components
import { Dropdown, Textarea } from '../../../../UiKit';
import Shimmer from 'sushiweb/Shimmer';
import { multiSelectStyles } from './constants';
import { getHeaderWithTooltip } from './HeaderTooltip';
import FailedMarkingUI from './FailedMarkingUI';
import MultiAddInput from './MultiAddInput';
import TabHeader from '../../Components/TabHeader/TabHeader';
import { UserRoundX } from 'lucide-react';

/**
 * component for marking users as fraud in bulk
 * @component
 */

const BulkMarkFraud = () => {
  // states for user id and cart id
  const [userIdText, setUserIdText] = useState('');
  const [parsedUserIds, setParsedUserIds] = useState([]);
  const [cartIdText, setCartIdText] = useState('');
  const [parsedCartIds, setParsedCartIds] = useState([]);

  // State for primary reasons management
  const [primaryReasonList, setPrimaryReasonList] = useState([]);
  const [primaryReasons, setPrimaryReasons] = useState([]);

  // State for secondary reasons management
  const [secondaryReasonList, setSecondaryReasonList] = useState([]);
  const [secondaryReasons, setSecondaryReasons] = useState([]);

  // State for expiry and comments
  const [expiryOptions, setExpiryOptions] = useState([]);
  const [expiryDays, setExpiryDays] = useState('');
  const [comment, setComment] = useState('');

  // state for entity types
  const [entityTypesList, setEntityTypesList] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);

  // state for storing failed mark fraud user ids and cart ids
  const [failedMarkings, setFailedMarkings] = useState({
    userIds: [],
    cartIds: [],
    actions: {},
  });

  const multiselectRef = useRef();

  // Fetch fraud parameters using react-query
  const markFraudEndPoint = 'user/bulk_mark_fraud';

  const {
    data: fraudParams,
    error: paramsError,
    isLoading: isParamsLoading,
    isError: isParamsError,
  } = useQueryCall(
    {
      onSuccess: (response) => {
        // Check for nested data structure
        if (!response?.reason_options) {
          toast.error('Invalid fraud parameters received');
          return;
        }

        // Transform primary reasons into correct format for Multiselect
        const primaryOptions = Object.keys(response.reason_options).map(
          (reason, index) => ({
            id: index,
            name: reason,
          }),
        );
        setPrimaryReasonList(primaryOptions);

        // Transform and set expiry options
        if (response.karma_expiry_options?.length) {
          setExpiryOptions(response.karma_expiry_options);
        }

        // Transform entity types into correct format for Multiselect
        const entityOptions = response.entity_types?.map((entity_type) => ({
          id: entity_type?.value,
          name: entity_type?.label,
        }));
        setEntityTypesList(entityOptions);
      },
    },
    markFraudEndPoint,
    ['getFraudParams'],
  );

  // Memoize transformed options to prevent unnecessary re-renders
  const memoizedExpiryOptions = useMemo(() => expiryOptions, [expiryOptions]);

  const { mutate: markFraud, isLoading: isMarking } = useMutationCall(
    {
      onSuccess: (data) => {
        if (data?.success) {
          if (
            data.failed_user_ids?.length > 0 ||
            data.failed_cart_ids?.length > 0 ||
            Object.keys(data.failed_actions || {}).length > 0
          ) {
            setFailedMarkings({
              userIds: data.failed_user_ids || [],
              cartIds: data.failed_cart_ids || [],
              actions: data.failed_actions || {},
            });
            toast.error(
              'Marked as fraud successfully, but some IDs failed to process.',
            );
          } else {
            toast.success('Marked as fraud successfully');
          }
        }
      },
    },
    markFraudEndPoint,
    [], // Not cached so that we can retry marking fraud
    {
      doNotShowSuccessToast: true,
    },
  );

  const refreshEligibleSecondaryReasons = (selectedList) => {
    // Get secondary reasons for all selected primary reasons
    const secondaryReasons = selectedList.flatMap(
      (item) => fraudParams.reason_options[item.name] || [],
    );

    // Update secondary reasons list
    setSecondaryReasonList(
      secondaryReasons.map((reason, index) => ({
        id: index,
        name: reason,
      })),
    );

    // Clear previous secondary selections
    setSecondaryReasons([]);
  };

  const handleSecondaryReasonUpdate = (selectedList) => {
    // Update secondary reasons state with the new selected list
    setSecondaryReasons(selectedList);
  };

  const handleEntityTypeUpdate = (selectedList) => {
    // Update entity types state with the new selected list
    setEntityTypes(selectedList);
  };

  const handlePrimaryReasonUpdate = (selectedList) => {
    // Update primary reasons state with the new selected list
    setPrimaryReasons(selectedList);

    // Refresh secondary reasons based on new selection
    refreshEligibleSecondaryReasons(selectedList);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (parsedUserIds?.length === 0 && parsedCartIds?.length === 0) {
      // If no user IDs or cart IDs are provided, show an error
      toast.error('Please provide at least one User ID or Cart ID');
    } else if (!secondaryReasons.length) {
      toast.error('Select at least one specific reason');
    } else if (expiryDays == '') {
      toast.error('Select expiry days');
    } else if (!entityTypes?.length) {
      toast.error('Select at least one entity type');
    } else {
      const dataToSubmit = {
        reason_codes: secondaryReasons?.map((reason) => reason?.name),
        comment: comment,
        expiry_days: memoizedExpiryOptions?.[expiryDays]?.value,
        entity_types: entityTypes?.map((entity) => entity?.id) || [],
        cart_ids: parsedCartIds || [],
        user_ids: parsedUserIds || [],
      };
      markFraud(dataToSubmit);
    }
  };

  const handleAddID = (idLabel, inputValue, setInputValue, setParsedIds) => {
    const trimmedInputValue = (inputValue ?? '').trim();
    if (trimmedInputValue === '') {
      toast.error(`Please enter a valid ${idLabel} ID`);
      return;
    }

    const parsedIDs = trimmedInputValue
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id !== '');

    const incorrectIDs = [];
    const correctedIDs = [];

    for (const id of parsedIDs) {
      if (id.includes(' ') || !/^\d+$/.test(id)) {
        incorrectIDs.push(id);
      } else {
        correctedIDs.push(id);
      }
    }

    if (incorrectIDs.length > 0) {
      toast.error(
        `Invalid ${idLabel} IDs: ${incorrectIDs.join(
          ', ',
        )}. ${idLabel} IDs should be numeric, comma-separated, and contain no spaces.`,
      );
    }

    if (correctedIDs.length > 0) {
      setParsedIds((prev) => {
        // Append new IDs to the existing ones
        const existingIds = prev ? prev?.map((id) => id.trim()) : [];
        // clear input field after adding
        setInputValue('');

        return [...new Set([...existingIds, ...correctedIDs])];
      });
    }
  };

  const handleRemoveID = (setterFunction, id) => {
    setterFunction((prev) => prev.filter((existingID) => existingID !== id));
  };

  useEffect(() => {
    const form = document.querySelector('form');
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
        e.preventDefault();
      }
    };

    if (form) {
      form.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (form) {
        form.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, []);

  if (isParamsError) {
    return (
      <div className='tw-w-full'>
        <EmptyState
          img={InvalidSearchImg}
          heading={paramsError?.message || 'Something went wrong!'}
          isPop={true}
        />
        ;
      </div>
    );
  }

  if (isParamsLoading) return <Shimmer height='20rem' width={'100%'} />;

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-gap-2'>
      <TabHeader
        title='Bulk Fraud Marking'
        icon={<UserRoundX size={20} strokeWidth={2.4} className='tw-ml-1 ' />}
        subTitle='Select all applicable reasons for marking customers as fraudulent.'
      />

      {fraudParams && (
        <form onSubmit={handleSubmit} className='tw-pb-20'>
          <div className='tw-mt-10 tw-mb-6 tw-flex tw-flex-col tw-gap-8'>
            {/* User Id Input field */}
            <MultiAddInput
              label='User ID'
              value={userIdText}
              onChangeHandler={setUserIdText}
              parsedIDs={parsedUserIds}
              handleAddID={() =>
                handleAddID('User', userIdText, setUserIdText, setParsedUserIds)
              }
              handleRemoveID={handleRemoveID}
              setParsedIDs={setParsedUserIds}
            />

            {/* Cart Id Input field */}
            <MultiAddInput
              label='Cart ID'
              value={cartIdText}
              onChangeHandler={setCartIdText}
              parsedIDs={parsedCartIds}
              handleAddID={() =>
                handleAddID('Cart', cartIdText, setCartIdText, setParsedCartIds)
              }
              handleRemoveID={handleRemoveID}
              setParsedIDs={setParsedCartIds}
            />
          </div>

          {/* Entity Types Selection Dropdown */}
          {entityTypesList?.length > 0 ? (
            <label htmlFor='entityTypes' css={multiSelectStyles}>
              {getHeaderWithTooltip(
                'Entity Type',
                'Select the type of entity you want to mark as fraud',
              )}
              <Multiselect
                ref={multiselectRef}
                avoidHighlightFirstOption
                options={entityTypesList || []}
                selectedValues={entityTypes}
                onSelect={handleEntityTypeUpdate}
                onRemove={handleEntityTypeUpdate}
                displayValue='name'
                placeholder='Search entity types...'
              />
            </label>
          ) : null}

          {/* Primary and Secondary Reasons */}
          <label htmlFor='primaryReasons' css={multiSelectStyles}>
            {getHeaderWithTooltip('Base Reasons')}
            <Multiselect
              ref={multiselectRef}
              avoidHighlightFirstOption
              options={primaryReasonList}
              selectedValues={primaryReasons}
              onSelect={handlePrimaryReasonUpdate}
              onRemove={handlePrimaryReasonUpdate}
              displayValue='name'
              placeholder='Search reasons...'
            />
          </label>

          <label htmlFor='secondaryReasons' css={multiSelectStyles}>
            {getHeaderWithTooltip(
              'Specific Reasons',
              'Select exact reasons corresponding to the base reason(s) selected',
            )}
            <Multiselect
              ref={multiselectRef}
              avoidHighlightFirstOption
              options={secondaryReasonList}
              selectedValues={secondaryReasons}
              onSelect={handleSecondaryReasonUpdate}
              onRemove={handleSecondaryReasonUpdate}
              displayValue='name'
              placeholder='Search reasons...'
            />
          </label>

          {/* Expiry Days */}
          <label htmlFor='expiryDays' css={multiSelectStyles}>
            {getHeaderWithTooltip(
              'Expiry Days',
              'For how many days should this customer be marked as fraud?',
            )}
            <Dropdown
              dropDownValues={memoizedExpiryOptions.map((opt) =>
                opt.label.toString(),
              )}
              onSelect={(value) => {
                setExpiryDays(value);
              }}
              dropdownVal={expiryDays.toString()}
              placeholder='Please Select Expiry Days'
            />
          </label>

          {/* Comments Section */}
          <label
            htmlFor='Comment'
            css={`
              margin-top: 2rem;
            `}
          >
            {getHeaderWithTooltip(
              'Remarks',
              'Add any extra information that helped identify fraudulent behavior',
            )}
            <Textarea
              placeholder='Add some remarks'
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
              }}
              maxLimit='300'
              showBtn={false}
            />
          </label>

          {/* Failed Marking  */}
          <FailedMarkingUI failedMarkings={failedMarkings} />

          {/* Submit Button */}
          <div
            css={`
              margin-top: 3rem;
              display: flex;
              justify-content: flex-end;
            `}
          >
            <button
              type='submit'
              disabled={isMarking || isParamsLoading}
              className='tw-text-[14px] tw-font-medium tw-bg-[#318616] tw-text-white tw-rounded-[0.6rem] tw-border-none tw-px-12 tw-py-4 tw-min-h-8 tw-leading-none tw-cursor-pointer disabled:tw-cursor-not-allowed disabled:tw-bg-[#cfcfcf]'
            >
              {isMarking ? (
                <div className='tw-w-full tw-justify-center tw-items-center tw-text-white'>
                  <Spinner color={'white'} />
                </div>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Update PropTypes to accept both string and number
BulkMarkFraud.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  closePopup: PropTypes.func.isRequired,
};

BulkMarkFraud.defaultProps = {
  closePopup: () => {},
};

export default BulkMarkFraud;
